import { gql } from '@apollo/client';

export const ALL_AUCTIONS_QUERY_NAME = 'AllAuctions';
export const ALL_AUCTIONS = gql`
    query ${ALL_AUCTIONS_QUERY_NAME}(
    $page: Int,
    $perPage: Int,
    $query: String,
    $sortKey: String,
    $sortDirection: String,
    $valueMapping: Map_String_List_MappedValueScalar,
    $timeZone: String,
    $auctionPhaseFilter: AuctionFilterType,
    $productTypeFilter: ProductTypeFilter,
    $auctionClosureTimeFilter: AuctionClosureTimeFilterType,
    ) {
        allAuctions(
            page: $page,
            perPage: $perPage,
            query: $query,
            sortKey: $sortKey,
            sortDirection: $sortDirection,
            valueMapping: $valueMapping,
            timeZone: $timeZone,
            auctionPhaseFilter: $auctionPhaseFilter,
            productTypeFilter: $productTypeFilter,
            auctionClosureTimeFilter: $auctionClosureTimeFilter,
        ) {
            availableProducts {
                productId
                name
                productType
                quantityUnit
            }
            availableZones {
                code
                name
                currency {
                    code
                }
            }
            availableAuctioneers {
                name
                memberId
                technicalAuctioneer
                regularAuctioneer
                euAuctioneer
            }
            availableSubmarkets {
                name
                code
            }
            availableAuctioneerDataTemplates {
                id
                name
                templateItems {
                    auctioneer {
                        memberId
                        name
                    }
                    submarket {
                        code
                        name
                    }
                    quantity
                }
            }
            availableAuctionTypes
            availableBiddingTypes
            availableTradePriceCalculationTypes
            availableAuctionAlgorithms
            availableAuctionAcceptanceTypes
            availableSettlStartTypes
            defaultAuctionDurationHours
            volumeDistributionStep
            availableProductTypes
            availableAuctionTemplates {
                id
                name
                product {
                    productId
                    productType
                    quantityUnit
                }
                description
                openingTime
                closureTime
                algorithm
                showLastPrice
                showRanking
                limitBidModification
                minPrice
                maxPrice
                spotReferencePrice
                absoluteDeviation
                relativeDeviation
                zone {
                    code
                    currency {
                        code
                    }
                }
                auctioneerMember {
                    memberId
                    euAuctioneer
                }
                auctionType
                biddingType
                auctionAcceptanceType
                tradePriceCalculationType
                quantity
                deliveryYear
                power
                smallestUnit
                settlStartType
                maxBidSize
                revenueSplit
            }
            auctionList {
                totalCount
                models {
                    id
                    name
                    zone {
                        id
                        code
                        name
                        flagCode
                        currency {
                            code
                        }
                    }
                    phase
                    product {
                        id
                        name
                        productId
                        isinCode
                        iconSymbol
                        iconColor
                        quantityUnit
                        productType
                    }
                    quantity
                    openingTime
                    closureTime
                    energyPerLot
                    biddingType
                    auctionType
                    scope
                }
            }
        }
    }
`;

export const AUCTION_DETAIL_RESPONSE_BODY= `
            id
            name
            zone {
                id
                code
                name
                flagCode
                currency {
                    code
                }
            }
            phase
            volumeDistributed
            product {
                id
                name
                productId
                isinCode
                iconSymbol
                iconColor
                quantityUnit
                productType
            }
            algorithm
            quantity
            auctioneerMember {
                memberId
                name
                euAuctioneer
                foreignCurrencyAllowed
            }
            auctioneerDataItems {
                auctioneer {
                    memberId
                    name
                }
                submarket {
                    code
                    name
                }
                quantity
            }
            openingTime
            closureTime
            fastMarket
            smallestUnit
            spotReferencePrice
            absoluteDeviation
            maxBidSize
            minPrice
            relativeDeviation
            showLastPrice
            showRanking
            limitBidModification
            energyPerLot
            description
            power
            maxPrice
            deliveryYear
            biddingType
            auctionType
            scope
            revenueSplit
            auctionResult {
                averageAcceptedPrice
                lastAcceptedPrice
                numberOfValidBids
                indicativePrice
                isUndersell
                totalBidQuantities
                finalPrice
                executedBidsPrice
                totalVolume
            }
            lowestAcceptablePrice
            highestAcceptablePrice
            roundUpHighestAcceptablePrice
            tradePriceCalculationType
            auctionAcceptanceType
            settlStartType
            smallestUnit
            fastMarket
            modificationBlockers
            acceptanceBlockers
`;

export const AUCTION_DETAIL_QUERY_NAME = 'AuctionDetail';
export const AUCTION_DETAIL = gql`
    query ${AUCTION_DETAIL_QUERY_NAME}(
        $auctionId: Int!,
    ) {
        auctionDetail(
            auctionId: $auctionId,
        ) {
            ${AUCTION_DETAIL_RESPONSE_BODY}
        }
    }
`;

export const ON_AUCTION_CHANGED = gql`
    subscription onAuctionChanged {
        auctionChanged {
            correlationId
            action
            model {
                id
                name
            }
        }
    }
`;

export const CREATE_AUCTION = gql`
    mutation CreateAuction(
        $name: String!, 
        $deliveryYear: Int, 
        $productId: String!,
        $zoneCode: String!,
        $auctioneerMemberId: String!,
        $quantity: Int!,
        $power: Int,
        $openingTime: Date!,
        $closureTime: Date!,
        $auctionType: AuctionType!,
        $biddingType: BiddingType!,
        $tradePriceCalculationType: TradePriceCalculationType!,
        $auctionAcceptanceType: AuctionAcceptanceType!,
        $auctionAlgorithm: AuctionAlgorithm!,
        $showLastPrice: Boolean,
        $showRanking: Boolean,
        $limitBidModification: Boolean,
        $smallestUnit: Int,
        $minPrice: Price,
        $maxPrice: Price,
        $spotReferencePrice: Price,
        $absoluteDeviation: Price,
        $relativeDeviation: BigDecimal,
        $roundUpHighestAcceptablePrice: Boolean,
        $description: String,
        $settlStartType: SettlStartType,
        $maxBidSize: Int,
        $fastMarket: Boolean,
        $auctioneerDataItems: [AuctioneerDataItemInputInput],
        $revenueSplit: Int,
        $correlationId: String!
    ) {
        createAuction(
            name: $name,
            deliveryYear: $deliveryYear,
            productId: $productId,
            zoneCode: $zoneCode,
            auctioneerMemberId: $auctioneerMemberId,
            quantity: $quantity,
            power: $power,
            openingTime: $openingTime,
            closureTime: $closureTime,
            auctionType: $auctionType,
            biddingType: $biddingType,
            tradePriceCalculationType: $tradePriceCalculationType,
            auctionAcceptanceType: $auctionAcceptanceType,
            auctionAlgorithm: $auctionAlgorithm,
            showLastPrice: $showLastPrice,
            showRanking: $showRanking,
            limitBidModification: $limitBidModification,
            smallestUnit: $smallestUnit,
            minPrice: $minPrice,
            maxPrice: $maxPrice,
            spotReferencePrice: $spotReferencePrice
            absoluteDeviation: $absoluteDeviation
            relativeDeviation: $relativeDeviation
            roundUpHighestAcceptablePrice: $roundUpHighestAcceptablePrice
            description: $description,
            settlStartType: $settlStartType,
            maxBidSize: $maxBidSize,
            fastMarket: $fastMarket,
            auctioneerDataItems: $auctioneerDataItems,
            revenueSplit: $revenueSplit,
            correlationId: $correlationId
        ) {
            id
            name
        }
    }
`;

export const UPDATE_AUCTION = gql`
    mutation UpdateAuction(
        $id: Int!,
        $name: String!, 
        $deliveryYear: Int, 
        $productId: String!,
        $zoneCode: String!,
        $auctioneerMemberId: String!,
        $quantity: Int!,
        $power: Int,
        $openingTime: Date!,
        $closureTime: Date!,
        $auctionType: AuctionType!,
        $biddingType: BiddingType!,
        $tradePriceCalculationType: TradePriceCalculationType!,
        $auctionAcceptanceType: AuctionAcceptanceType!,
        $auctionAlgorithm: AuctionAlgorithm!,
        $showLastPrice: Boolean,
        $showRanking: Boolean,
        $limitBidModification: Boolean,
        $smallestUnit: Int,
        $minPrice: Price,
        $maxPrice: Price,
        $spotReferencePrice: Price,
        $absoluteDeviation: Price,
        $relativeDeviation: BigDecimal,
        $roundUpHighestAcceptablePrice: Boolean,
        $description: String,
        $settlStartType: SettlStartType,
        $maxBidSize: Int,
        $fastMarket: Boolean,
        $auctioneerDataItems: [AuctioneerDataItemInputInput],
        $revenueSplit: Int,
        $extraConfirmation: Boolean = false,
        $fourEyesUserId: String, 
        $fourEyesPassword: String, 
        $correlationId: String!
    ) {
        updateAuction(
            id: $id,
            name: $name,
            deliveryYear: $deliveryYear,
            productId: $productId,
            zoneCode: $zoneCode,
            auctioneerMemberId: $auctioneerMemberId,
            quantity: $quantity,
            power: $power,
            openingTime: $openingTime,
            closureTime: $closureTime,
            auctionType: $auctionType,
            biddingType: $biddingType,
            tradePriceCalculationType: $tradePriceCalculationType,
            auctionAcceptanceType: $auctionAcceptanceType,
            auctionAlgorithm: $auctionAlgorithm,
            showLastPrice: $showLastPrice,
            showRanking: $showRanking,
            limitBidModification: $limitBidModification,
            smallestUnit: $smallestUnit,
            minPrice: $minPrice,
            maxPrice: $maxPrice,
            spotReferencePrice: $spotReferencePrice
            absoluteDeviation: $absoluteDeviation
            relativeDeviation: $relativeDeviation
            roundUpHighestAcceptablePrice: $roundUpHighestAcceptablePrice
            description: $description,
            settlStartType: $settlStartType,
            maxBidSize: $maxBidSize,
            fastMarket: $fastMarket,
            auctioneerDataItems: $auctioneerDataItems,
            revenueSplit: $revenueSplit,
            extraConfirmation: $extraConfirmation,
            fourEyesUserId: $fourEyesUserId, 
            fourEyesPassword: $fourEyesPassword, 
            correlationId: $correlationId
        ) {
            name
        }
    }
`;

export const CALCULATE_ENERGY_PER_LOT = gql`
    query calculateEnergyPerLot(
        $year: String, 
        $productId: String, 
        $power: String
    ) {
        calculateEnergyPerLot(
            year: $year, 
            productId: $productId, 
            power: $power
        )
    }
`;

export const PUBLISH_AUCTION = gql`
    mutation PublishAuction(
        $id: Int!, 
        $fourEyesUserId: String,
        $fourEyesPassword: String,
        $correlationId: String!
    ) {
        publishAuction(
            id: $id, 
            fourEyesUserId: $fourEyesUserId,
            fourEyesPassword: $fourEyesPassword,
            correlationId: $correlationId
        ) {
            name
        }
    }
`;

export const OPEN_AUCTION = gql`
    mutation OpenAuction(
        $id: Int!,
        $fourEyesUserId: String,
        $fourEyesPassword: String,         
        $correlationId: String!
    ) {
        openAuction(
            id: $id, 
            fourEyesUserId: $fourEyesUserId,
            fourEyesPassword: $fourEyesPassword,
            correlationId: $correlationId
        ) {
            name
        }
    }
`;

export const CLOSE_AUCTION = gql`
    mutation CloseAuction(
        $id: Int!,
        $extraConfirmation: Boolean = false,
        $fourEyesUserId: String,
        $fourEyesPassword: String,        
        $correlationId: String!
    ) {
        closeAuction(
            id: $id,
            extraConfirmation: $extraConfirmation,
            fourEyesUserId: $fourEyesUserId,
            fourEyesPassword: $fourEyesPassword,
            correlationId: $correlationId
        ) {
            name
        }
    }
`;

export const ACCEPT_AUCTION = gql`
    mutation AcceptAuction(
        $id: Int!,
        $fourEyesUserId: String,
        $fourEyesPassword: String,
        $correlationId: String!,
    ) {
        acceptAuction(
            id: $id,
            fourEyesUserId: $fourEyesUserId,
            fourEyesPassword: $fourEyesPassword,
            correlationId: $correlationId,
        ) {
            name
        }
    }
`;

export const REJECT_AUCTION = gql`
    mutation RejectAuction(
        $id: Int!,
        $fourEyesUserId: String,
        $fourEyesPassword: String,
        $correlationId: String!,
    ) {
        rejectAuction(
            id: $id,
            fourEyesUserId: $fourEyesUserId,
            fourEyesPassword: $fourEyesPassword,
            correlationId: $correlationId,
        ) {
            name
        }
    }
`;

export const REOPEN_AUCTION = gql`
    mutation ReopenAuction(
        $id: Int!,
        $closureTime: Date!,
        $extraConfirmation: Boolean = false,
        $fourEyesUserId: String,
        $fourEyesPassword: String,
        $correlationId: String!,
    ) {
        reopenAuction(
            id: $id,
            closureTime: $closureTime,
            extraConfirmation: $extraConfirmation,
            fourEyesUserId: $fourEyesUserId,
            fourEyesPassword: $fourEyesPassword,
            correlationId: $correlationId,
        ) {
            name
        }
    }
`;

export const CANCEL_AUCTION = gql`
    mutation CancelAuction(
        $id: Int!, 
        $fourEyesUserId: String,
        $fourEyesPassword: String,
        $correlationId: String!
    ) {
        cancelAuction(
            id: $id, 
            fourEyesUserId: $fourEyesUserId,
            fourEyesPassword: $fourEyesPassword,
            correlationId: $correlationId
        ) {
            name
        }
    }
`;

export const UPCOMING_AUCTIONS_FOR_DISTRIBUTION_QUERY_NAME = 'UpcomingAuctionsForDistribution';
const AUCTIONEER_DATA_ITEMS_BODY = `
            auctioneerDataItems {
                auctioneer {
                    memberId
                    name
                }
                submarket {
                    code
                    name
                }
                quantity
            }
`;
export const UPCOMING_AUCTIONS_FOR_DISTRIBUTION = gql`
    query ${UPCOMING_AUCTIONS_FOR_DISTRIBUTION_QUERY_NAME}(
        $cancelledAuctionId: Int!,
    ) {
        cancelledAuction: auctionDetail(
            auctionId: $cancelledAuctionId,
        ) {
            id
            quantity
            ${AUCTIONEER_DATA_ITEMS_BODY}
        }
        upcomingAuctionsForDistribution(
            cancelledAuctionId: $cancelledAuctionId,
        ) {
            id
            name
            openingTime
            quantity
            zone {
                id
                code
                name
            }
            ${AUCTIONEER_DATA_ITEMS_BODY}
        }
    }
`;

export const DISTRIBUTE_VOLUME = gql`
    mutation DistributeVolume(
        $cancelledAuctionId: Int!,
        $distributions: [VolumeDistributionInputInput],
        $fourEyesUserId: String,
        $fourEyesPassword: String,
        $correlationId: String!,
    ) {
        distributeVolume(
            cancelledAuctionId: $cancelledAuctionId,
            distributions: $distributions,
            fourEyesUserId: $fourEyesUserId,
            fourEyesPassword: $fourEyesPassword,
            correlationId: $correlationId,
        ) {
            name
        }
    }
`;
